import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import {
  Education,
  getAllEducation,
  createEducation,
  updateEducation,
  deleteEducation,
  updateEducationOrder,
} from "../firebase/educationService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

interface EducationContextType {
  education: Education[];
  loading: boolean;
  error: string | null;
  refreshEducation: () => Promise<void>;
  addEducation: (
    education: Omit<Education, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editEducation: (
    id: string,
    education: Partial<Omit<Education, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeEducation: (id: string) => Promise<void>;
  reorderEducation: (
    reorderedEducation: { id: string; order: number }[]
  ) => Promise<void>;
}

const EducationContext = createContext<EducationContextType | undefined>(
  undefined
);

export const useEducation = (): EducationContextType => {
  const context = useContext(EducationContext);
  if (!context) {
    throw new Error("useEducation must be used within an EducationProvider");
  }
  return context;
};

interface EducationProviderProps {
  children: ReactNode;
}

export const EducationProvider: React.FC<EducationProviderProps> = ({
  children,
}) => {
  const [education, setEducation] = useState<Education[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const fetchEducation = useCallback(
    async (forceRefresh: boolean = false) => {
      // Check if we have cached data and it's still fresh
      const now = Date.now();
      const isCacheValid =
        education.length > 0 && now - lastFetchTime < CACHE_DURATION;

      if (!forceRefresh && isCacheValid) {
        console.log("Using cached education data");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Use retry mechanism for fetching education
        const educationData = await withFirebaseRetry(async () =>
          getAllEducation()
        );

        setEducation(educationData);
        setLastFetchTime(now);
      } catch (err) {
        console.error("Error fetching education data:", err);
        setError("Failed to load education data. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [education.length, lastFetchTime]
  );

  // Initial fetch
  // Fetch once on mount. Do NOT depend on `fetchEducation` — its useCallback deps
  // include `lastFetchTime`, which the fetch updates, causing an infinite re-fetch
  // loop when the collection is empty (the length-based cache guard never trips).
  useEffect(() => {
    fetchEducation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshEducation = async () => {
    await fetchEducation(true); // Force refresh
  };

  const addEducation = async (
    educationData: Omit<Education, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-add")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    const id = await withFirebaseRetry(async () =>
      createEducation(educationData)
    );

    // Update state directly instead of full refresh
    const newEducation: Education = {
      ...educationData,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    setEducation((prev) => [...prev, newEducation]);

    return id;
  };

  const editEducation = async (
    id: string,
    educationData: Partial<Omit<Education, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-edit")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => updateEducation(id, educationData));

    // Update state directly instead of full refresh
    const updatedEducation = { ...educationData, updatedAt: Timestamp.now() };

    setEducation((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, ...updatedEducation } : item
      )
    );
  };

  const removeEducation = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-remove")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => deleteEducation(id));

    // Update state directly instead of full refresh
    setEducation((prev) => prev.filter((item) => item.id !== id));
  };

  const reorderEducation = async (
    reorderedEducation: { id: string; order: number }[]
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-reorder")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () =>
      updateEducationOrder(reorderedEducation)
    );

    // For reorder operations, we need to refresh to get the correct order
    await fetchEducation(true);
  };

  const value = {
    education,
    loading,
    error,
    refreshEducation,
    addEducation,
    editEducation,
    removeEducation,
    reorderEducation,
  };

  return (
    <EducationContext.Provider value={value}>
      {children}
    </EducationContext.Provider>
  );
};

export default EducationContext;
